import { sanitizeNullStrings } from "@/utils/sanitize";
import { useUTLSettingsContext } from "@dexteel/mesf-core";
import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { DelayCategorySelector } from "@/shared/selectors/DelayCategorySelector";
import { DelayCodeTreePicker } from "@/shared/selectors/DelayCodeTreePicker";
import { CatalogDelayCode } from "../models/CatalogDelayCode";
import { useConfigurationDelayCodeContext } from "../context/ConfigurationDelayCodeContext";
import { useDelayCodes } from "../hooks/delays/useDelayCodes";
import {
	getDelay,
	upsertDelay,
} from "../repositories/ConfigurationDelayRepository";

interface FormErrors {
	DelayCodeName?: string;
	ParentDelayCodeId?: string;
	DelayCategoryId?: string;
}

interface DelayCodeDetailViewerProps {
	delayCode: CatalogDelayCode | null;
	isCreating: boolean;
	isSaving: boolean;
	onSave: (delayCode: CatalogDelayCode) => void;
	onClose: () => void;
	onError: (msg: string) => void;
	onNotification: (msg: string) => void;
	onToggleActive: (node: CatalogDelayCode) => void;
}

export const DelayCodeDetailViewer = ({
	delayCode,
	isCreating,
	isSaving,
	onSave,
	onClose,
	onError,
	onNotification,
	onToggleActive,
}: DelayCodeDetailViewerProps) => {
	console.log("[RENDER] DelayCodeDetailViewer");
	const { state } = useUTLSettingsContext();
	const undeclaredId = Number(
		get(state, "settings.DELAYS_UNDECLARED_MILL", 11),
	);
	const undeclaredColdShearId = Number(
		get(state, "settings.DELAYS_UNDECLARED_COLD_SHEAR", 12),
	);

	const {
		state: { delayAreaAsset, allDelayCodes, delayNodeSelectedInTree },
	} = useConfigurationDelayCodeContext();

	const { loadFilterDelays } = useDelayCodes();

	const [formData, setFormData] = useState<CatalogDelayCode | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});
	const [isLoadingDetail, setIsLoadingDetail] = useState(false);
	const [isSavingInline, setIsSavingInline] = useState(false);

	const isNew = isCreating;

	const validateForm = useMemo(() => {
		return (data: CatalogDelayCode | null): FormErrors => {
			const newErrors: FormErrors = {};
			if (!data?.DelayCodeName || data.DelayCodeName.trim().length === 0) {
				newErrors.DelayCodeName = "Name is required";
			}
			if (
				data?.ParentDelayCodeId === undeclaredId ||
				data?.ParentDelayCodeId === undeclaredColdShearId
			) {
				newErrors.ParentDelayCodeId = "Undeclared cannot be used as a Parent";
			}
			if (!data?.DelayCategoryId) {
				newErrors.DelayCategoryId = "Category is required";
			}
			return newErrors;
		};
	}, [undeclaredId, undeclaredColdShearId]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;

	useEffect(() => {
		if (!delayCode && !isCreating) {
			setFormData(null);
			setErrors({});
			setTouched({});
			return;
		}

		loadFilterDelays();

		if (isCreating) {
			// New delay code
			const newData: CatalogDelayCode = {
				DelayCodeId: null,
				DelayCodeName: "",
				DelayCategoryId: null,
				DelayCategoryName: null,
				DelayCategoryColor: null,
				ParentDelayCodeId: null,
				DelayAreaAssetId: delayAreaAsset?.AssetId ?? null,
				IsAtTop: false,
				IsAtBottom: false,
				IsBranch: false,
				SortOrder: null,
				IsSelectable: true,
				IsEditable: true,
				IsActive: true,
				QuickButtonEnabled: false,
			};

			// If a node was selected, use it as parent
			if (
				delayNodeSelectedInTree?.ParentDelayCodeId &&
				delayNodeSelectedInTree?.IsEditable
			) {
				newData.ParentDelayCodeId = delayNodeSelectedInTree.DelayCodeId;
				newData.DelayCategoryId = delayNodeSelectedInTree.DelayCategoryId;
			} else {
				const parentRoot = allDelayCodes.find(
					(node: CatalogDelayCode) => node.ParentDelayCodeId === null,
				);
				if (parentRoot) {
					newData.ParentDelayCodeId = parentRoot.DelayCodeId;
				}
			}

			setFormData(newData);
			setErrors({});
			setTouched({});
		} else if (delayCode) {
			// Load existing delay code detail
			setIsLoadingDetail(true);
			(async () => {
				const response = await getDelay(delayCode.DelayCodeId!);
				if (response.ok) {
					const loaded = get(
						response,
						"data.tables[0].rows[0]",
						null,
					) as CatalogDelayCode | null;
					if (loaded) {
						setFormData(sanitizeNullStrings(loaded));
					} else {
						setFormData(sanitizeNullStrings(delayCode));
					}
				} else {
					onError(response.message);
					setFormData(sanitizeNullStrings(delayCode));
				}
				setIsLoadingDetail(false);
			})();
			setErrors({});
			setTouched({});
		}
	}, [delayCode, isCreating]);

	const handleChange = (field: keyof CatalogDelayCode, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof CatalogDelayCode) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = async (
		field: keyof CatalogDelayCode,
		value: unknown,
	) => {
		handleBlur(field);

		if (isNew) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = delayCode?.[field];
		if (originalValue === value) return;

		if (updatedData) {
			setIsSavingInline(true);
			const resp = await upsertDelay(updatedData);
			if (resp.ok) {
				onNotification("Delay code updated successfully");
				onSave(updatedData);
			} else {
				onError(resp.message);
			}
			setIsSavingInline(false);
		}
	};

	const handleCategoryChange = async (categoryId: number | null) => {
		handleChange("DelayCategoryId", categoryId);

		if (isNew) return;

		const updatedData = formData
			? { ...formData, DelayCategoryId: categoryId }
			: null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors.DelayCategoryId) return;

		if (delayCode?.DelayCategoryId === categoryId) return;

		if (updatedData) {
			setIsSavingInline(true);
			const resp = await upsertDelay(updatedData);
			if (resp.ok) {
				onNotification("Delay code updated successfully");
				onSave(updatedData);
			} else {
				onError(resp.message);
			}
			setIsSavingInline(false);
		}
	};

	const handleParentChange = async (parentId: number | null) => {
		handleChange("ParentDelayCodeId", parentId);

		if (isNew) return;

		if (parentId === undeclaredId || parentId === undeclaredColdShearId) {
			setTouched((prev) => ({ ...prev, ParentDelayCodeId: true }));
			setErrors((prev) => ({
				...prev,
				ParentDelayCodeId: "Undeclared cannot be used as a Parent",
			}));
			return;
		}

		if (delayCode?.ParentDelayCodeId === parentId) return;

		const updatedData = formData
			? { ...formData, ParentDelayCodeId: parentId }
			: null;

		if (updatedData) {
			setIsSavingInline(true);
			const resp = await upsertDelay(updatedData);
			if (resp.ok) {
				onNotification("Delay code updated successfully");
				onSave(updatedData);
			} else {
				onError(resp.message);
			}
			setIsSavingInline(false);
		}
	};

	const handleCreate = () => {
		setTouched({
			DelayCodeName: true,
			DelayCategoryId: true,
			ParentDelayCodeId: true,
		});
		setErrors(formErrors);

		if (formData && isFormValid) {
			onSave(formData);
		}
	};

	if (!delayCode && !isCreating) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a delay code to view details</Typography>
			</Box>
		);
	}

	if (isLoadingDetail) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
				}}
			>
				<CircularProgress size={32} />
			</Box>
		);
	}

	if (!formData) return null;

	const saving = isSaving || isSavingInline;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNew ? "New Delay Code" : formData.DelayCodeName}
					</Typography>
					{saving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNew && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!isFormValid || saving}
						>
							{saving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={saving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1.5 }} />

			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12 }}>
					<Typography
						sx={{
							color: "primary.main",
							fontWeight: 600,
							fontSize: "0.85rem",
							mb: 0.5,
						}}
					>
						Basic Information
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 4 }}>
					<TextField
						label="Asset"
						value={delayAreaAsset?.AssetName ?? ""}
						disabled
						size="small"
						fullWidth
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 8 }}>
					<TextField
						label="Delay Code Name"
						value={formData.DelayCodeName}
						onChange={(e) => handleChange("DelayCodeName", e.target.value)}
						onBlur={(e) => handleBlurSave("DelayCodeName", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.DelayCodeName && !!errors.DelayCodeName}
						helperText={touched.DelayCodeName && errors.DelayCodeName}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				{(formData.ParentDelayCodeId !== null || isNew) && (
					<Grid2 size={{ xs: 12 }}>
						<DelayCodeTreePicker
							label="Parent"
							assetId={formData.DelayAreaAssetId}
							value={formData.ParentDelayCodeId}
							onChange={(value) => handleParentChange(value)}
							fieldError={
								touched.ParentDelayCodeId && errors.ParentDelayCodeId
									? {
											type: "validate",
											message: errors.ParentDelayCodeId,
										}
									: undefined
							}
						/>
					</Grid2>
				)}

				<Grid2 size={{ xs: 12 }}>
					<DelayCategorySelector
						label="Category"
						value={formData.DelayCategoryId}
						onChange={(value: number | null) => handleCategoryChange(value)}
						fieldError={
							touched.DelayCategoryId && errors.DelayCategoryId
								? {
										type: "validate",
										message: errors.DelayCategoryId,
									}
								: undefined
						}
						disabled={!isNew && !formData.IsEditable}
						required
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={!!formData.IsActive}
								onChange={(e) => {
									if (isNew) {
										handleChange("IsActive", e.target.checked);
									} else {
										onToggleActive(formData);
									}
								}}
								disabled={!isNew && !formData.IsEditable}
								size="small"
							/>
						}
						label="Active"
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
